import '@sagi.io/globalthis';
import 'url-search-params-polyfill';

import { Device } from '@cardsjd/device';

/**
 * @returns {string}
 */
function getRandomUUID() {
	return (Math.floor(Math.random() * (99999999 - 10000000 + 1)) + 10000000).toString(); //get random number:Math.floor(Math.random() * (max - min + 1)) + min;
}

export default class Analytics {
	appName = undefined;
	version = undefined;
	os = undefined;
	/** @default null */
	sessionid = null;
	/** @default null */
	uuid = null;
	/** @default null */
	mode = null;

	constructor(options) {
		this.appName = options?.appName || 'NoName';
		this.version = options?.version || '1.0.0';
		this.os = 'unknown';
	}

	/**
	 * @param {string} type
	 * @returns {boolean}
	 */
	storageAvailable(type) {
		try {
			const storage = globalThis[type], x = '__storage_test__';
			storage.setItem(x, x);
			storage.removeItem(x);
			return true;
		} catch (e) {
			return false;
		}
	}

	/**
	 * @returns {string}
	 */
	getUserID() {
		let uuid = null;

		//try using cordova
		if (typeof globalThis.device !== 'undefined' && globalThis.device.uuid !== 'undefined') {
			uuid = globalThis.device.uuid;
		}

		//if device id not exist create one and save it
		if (uuid === null && this.storageAvailable('localStorage')) {
			uuid = localStorage.getItem('uuid');
			if (uuid === null) {
				uuid = getRandomUUID();
				localStorage.setItem('uuid', uuid);
			}
		}

		//else just give it an id
		if (uuid === null) {
			uuid = getRandomUUID();
		}

		return uuid;

	}

	/**
	 * @returns {string}
	 */
	getSessionID() {
		let sessionid = null;
		//get session id
		if (sessionid === null && this.storageAvailable('sessionStorage')) {
			sessionid = sessionStorage.getItem('sessionid');
			if (sessionid === null) {
				sessionid = getRandomUUID();
				sessionStorage.setItem('sessionid', sessionid);
			}
		}

		if (sessionid === null) {
			sessionid = getRandomUUID(); //get random number:Math.floor(Math.random() * (max - min + 1)) + min;
		}

		return sessionid;
	}
	getPlatform() {
		const device = new Device();
		return device.getPlatform();
	}
	/**
	 * @param {string} appName
	 * @param {string} version
	 * @param {string} os
	 * @param {string} uuid
	 * @param {string} mode
	 * @returns {void}
	 */
	trackStart(appName, version, os, uuid, mode) {

		if (appName) {
			this.appName = appName;
		}

		if (version) {
			this.version = version;
		}

		if (os) {
			this.os = os;
		}

		if (!os) {
			this.os = this.getPlatform();
		}

		if (uuid) {
			this.uuid = uuid;
		}

		if (!uuid) {
			this.uuid = this.getUserID();
		}

		this.sessionid = this.getSessionID();
		if (!mode) {
			this.mode = mode;
		}

		this.track('Start');
	}

	/**
	 * @param {string | number | undefined} page
	 * @param {string | undefined} urlParameters
	 * @returns {void}
	 */
	trackPage(page, urlParameters) {
		this.track('PageView', page, urlParameters);
	}

	/**
	 * @param {string} message
	 * @param {string} source
	 * @param {string} lineno
	 * @param {string} colno
	 * @param {{ stack: string; }} error
	 * @param {string[]} logs
	 * @returns {void}
	 */
	trackError(message, source, lineno, colno, error, logs) {
		let stack = '';
		if (error && error.stack) {
			stack = error.stack;
		}

		let log = '';
		if (logs) {
			log = logs.join(', ');
		}

		let details = `error ${error} : stack ${stack} : source ${source} : lineno ${lineno} : colno ${colno} : logs ${log}`;

		details = details.substring(0, 1500);

		//get uuid incase it never loaded
		if (!this.os) {
			this.os = getPlatform();
		}

		if (!this.uuid) {
			this.uuid = this.getUserID();
		}

		if (!this.sessionid) {
			this.sessionid = this.getSessionID();
		}

		this.track('Error', message, details);
	}

	/**
	 * @param {string} Event
	 * @param {string | number} [parameter1]
	 * @param {string | undefined} [parameter2]
	 * @returns {void}
	 */
	track(Event, parameter1, parameter2) {
		if (!this.uuid) {
			return;
		}

		const appName = this.appName;
		const version = this.version;
		const os = this.os;
		const uuid = this.uuid;
		const sessionid = this.sessionid;
		const mode = this.mode;

		//get CurrentCulture
		const currentCulture = globalThis.navigator?.userLanguage ?? globalThis.navigator?.language; //works IE/SAFARI/CHROME/FF

		this.sendData(appName, version, Event, parameter1, parameter2, currentCulture, sessionid, uuid, os, mode);
	}

	/**
	 * @param {string} AppName
	 * @param {string} Version
	 * @param {string} Event
	 * @param {string | number | undefined} parameter1
	 * @param {string | number | undefined} parameter2
	 * @param {string} CurrentCulture
	 * @param {string | undefined | null} SessionID
	 * @param {string} DeviceUniqueID
	 * @param {string} OS
	 * @param {string | undefined | null} mode
	 * @returns {void}
	 */
	sendData(AppName, Version, Event, parameter1, parameter2, CurrentCulture, SessionID, DeviceUniqueID, OS, mode) {

		//send data
		const theUrl = 'https://analytics.jdsoftwarellc.com/Service.svc/LogEvent?';

		// if (window.location.protocol === "http:")
		//     theUrl = "http://analytics.jdsoftwarellc.com/WP7/Service.svc/LogEvent?";

		//if undefined return string
		if (!CurrentCulture) {
			CurrentCulture = '';
		}

		if (!DeviceUniqueID) {
			DeviceUniqueID = '';
		}

		if (!SessionID) {
			SessionID = '';
		}

		if (!Version) {
			Version = '';
		}

		if (!parameter1) {
			parameter1 = '';
		}

		if (!parameter2) {
			parameter2 = '';
		}

		if (!OS) {
			OS = '';
		}

		if (!mode) {
			mode = '';
		}

		const params = new URLSearchParams();
		params.append('AppName', AppName);
		params.append('Event', Event);
		params.append('Culture', CurrentCulture);
		params.append('DeviceUniqueID', DeviceUniqueID);
		params.append('SessionID', SessionID);
		params.append('Version', Version);
		params.append('Parameter1', parameter1.toString());
		params.append('Parameter2', parameter2.toString());
		params.append('LiveAnonymousID', ''); //LiveAnonymousID);
		params.append('OS', OS.toString());
		params.append('Mode', mode);
		params.append('Random', Math.floor(Math.random() * 1000000).toString());

		try {
			const xmlHttp = new XMLHttpRequest();
			xmlHttp.open('GET', `${theUrl}${params.toString()}`, true);
			xmlHttp.send(null);
		} catch (ex) {
			//log error, but in this case we dont care
			// const error = ex;
		}

	}
}

/**
 * @typedef {Object} AnalyticsOptions
 * @property {string} [appName]
 * @property {string} [version] 
 */
